import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseUsd, usd } from '../api/client';
import { useCreateEvent, useDeleteEvent, useEvents, useLots } from '../api/hooks';
import {
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  Loading,
  PageHeader,
  Panel,
  Table,
  Td,
  Th,
} from '../components/ui';
import { useApiError } from '../i18n/useApiError';

const localInput = (date: Date) =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);

/**
 * Event pricing (FR-A-19 ~ A-21). A game-day flat rate overrides every other
 * rule — without it, lots near a stadium simply will not join the platform.
 */
export function EventsPage({ canEdit }: { canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const describeError = useApiError();
  const events = useEvents();
  const lots = useLots();
  const create = useCreateEvent();
  const remove = useDeleteEvent();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [price, setPrice] = useState('55.00');
  const [startsAt, setStartsAt] = useState(localInput(new Date()));
  const [endsAt, setEndsAt] = useState(localInput(new Date(Date.now() + 6 * 3_600_000)));
  const [lotIds, setLotIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    try {
      await create.mutateAsync({
        name,
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        priceCents: parseUsd(price),
        lotIds: lotIds.length > 0 ? lotIds : (lots.data?.lots ?? []).map((lot) => lot.id),
      });
      setOpen(false);
      setName('');
    } catch (e) {
      setError(describeError(e, 'events.createFailed'));
    }
  };

  if (events.isPending) return <Loading />;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('events.title')}
        hint={t('events.hint')}
        actions={
          canEdit ? (
            <Button variant="primary" onClick={() => setOpen((v) => !v)}>
              {t('events.new')}
            </Button>
          ) : undefined
        }
      />

      {open ? (
        <Card className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label={t('events.name')}>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Bears vs Packers" />
            </Field>
            <Field label={t('events.startsAt')}>
              <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            </Field>
            <Field label={t('events.endsAt')}>
              <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            </Field>
            <Field label={t('events.price')}>
              <Input className="num" value={price} onChange={(e) => setPrice(e.target.value)} />
            </Field>
          </div>

          <Field label={t('events.lots')}>
            <div className="flex flex-wrap gap-2">
              {(lots.data?.lots ?? []).map((lot) => {
                const selected = lotIds.includes(lot.id);
                return (
                  <button
                    key={lot.id}
                    type="button"
                    onClick={() =>
                      setLotIds((current) =>
                        selected ? current.filter((id) => id !== lot.id) : [...current, lot.id],
                      )
                    }
                    className={`rounded-full border px-3 py-1.5 text-xs ${
                      selected ? 'border-ink bg-ink text-white' : 'border-hairline'
                    }`}
                  >
                    {lot.name}
                  </button>
                );
              })}
            </div>
          </Field>

          {error ? <ErrorNote message={error} /> : null}

          <div className="flex gap-2">
            <Button variant="dark" onClick={() => void submit()} disabled={create.isPending || !name}>
              {t('events.saveEvent')}
            </Button>
            <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
          </div>
        </Card>
      ) : null}

      <Table>
        <thead>
          <tr>
            <Th>{t('events.colName')}</Th>
            <Th>{t('events.colDate')}</Th>
            <Th>{t('events.colTime')}</Th>
            <Th align="right">{t('events.colPrice')}</Th>
            <Th>{t('events.colLots')}</Th>
            {canEdit ? (
              <Th align="right" sticky>
                {t('common.actions')}
              </Th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {events.data?.events.map((event) => {
            const start = new Date(event.startsAt);
            const end = new Date(event.endsAt);
            return (
              <tr key={event.id}>
                <Td strong>{event.name}</Td>
                <Td mono>{start.toLocaleDateString(i18n.language, { month: 'numeric', day: 'numeric' })}</Td>
                <Td mono>
                  {start.toLocaleTimeString(i18n.language, {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  })}{' '}
                  –{' '}
                  {end.toLocaleTimeString(i18n.language, {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  })}
                </Td>
                <Td align="right" mono strong>
                  {usd(event.priceCents)}
                </Td>
                <Td>{event.lots.map((lot) => lot.name).join(' / ') || t('common.all')}</Td>
                {canEdit ? (
                  <Td align="right" sticky>
                    <Button variant="ghost" onClick={() => remove.mutate(event.id)}>
                      {t('common.delete')}
                    </Button>
                  </Td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </Table>

      <Panel>{t('events.panel')}</Panel>
    </div>
  );
}
